import RecipeImage from '../components/RecipeImage';
import AuthorLink from '../components/AuthorLink';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Box,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Button,
    Grid,
    ListItemText,
    Skeleton,
    Typography,
    useMediaQuery,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    Alert,
    TextField,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Popover,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import CheckIcon from '@mui/icons-material/Check';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import UploadImageDialog from '../components/UploadImageDialog';
import { useReactToPrint } from 'react-to-print';
import { uploadImage } from '../utils/uploadImage';
import { refreshRecipes } from '../utils/refreshRecipes';
import RecipeEdit from '../components/RecipeEdit';
import RecipeHistory from '../components/RecipeHistory';
import RecipePrint from '../components/RecipePrint';
import ExportOptionsDialog from '../components/ExportOptionsDialog';
import { fetchCollections, addToCollection, removeFromCollection } from '../utils/collectionsApi';
import { isInSession, addToSession, getActiveRecipes } from '../utils/cookSession';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import RecipeComments from '../components/RecipeComments';

// Converts a fraction string like "1/2" or mixed "2 1/4" to a decimal
function parseFraction(str) {
    if (!str) return null;
    const trimmed = str.trim();
    // Mixed number: "2 1/4"
    const mixed = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
    // Simple fraction: "1/2"
    const frac = trimmed.match(/^(\d+)\/(\d+)$/);
    if (frac) return parseInt(frac[1]) / parseInt(frac[2]);
    // Plain number
    const num = parseFloat(trimmed);
    return isNaN(num) ? null : num;
}

// Converts a decimal back to a readable fraction string
function toFractionString(value) {
    const FRACTIONS = [
        [1, '1'], [3/4, '3/4'], [2/3, '2/3'], [1/2, '1/2'],
        [1/3, '1/3'], [1/4, '1/4'], [1/8, '1/8'],
    ];
    const whole = Math.floor(value);
    const remainder = value - whole;
    if (remainder < 0.05) return whole > 0 ? `${whole}` : '0';
    // Find closest fraction
    let closest = FRACTIONS[0];
    let minDiff = Math.abs(remainder - FRACTIONS[0][0]);
    for (const f of FRACTIONS) {
        const diff = Math.abs(remainder - f[0]);
        if (diff < minDiff) { minDiff = diff; closest = f; }
    }
    return whole > 0 ? `${whole} ${closest[1]}` : closest[1];
}

function scaleQuantity(quantity, multiplier) {
    if (!quantity || multiplier === 1) return quantity;
    const num = parseFraction(quantity);
    if (num === null) return quantity; // can't parse (e.g. "a pinch"), return as-is
    const scaled = num * multiplier;
    // If result is a whole number, show it cleanly
    if (Math.abs(scaled - Math.round(scaled)) < 0.05) return `${Math.round(scaled)}`;
    return toFractionString(scaled);
}

const SectionHeader = ({ children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>{children}</Typography>
        <Divider />
    </Box>
);

const Recipe = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const tracked = useMemo(() => JSON.parse(sessionStorage.getItem('recipes')) || { recipes: {} }, []);
    const { id } = useParams();
    const navigate = useNavigate();

    const [recipeData, setRecipeData] = useState(tracked.recipes[id] || null);
    const [loading, setLoading] = useState(!tracked.recipes[id]);
    const [error, setError] = useState(null);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [hasHistory, setHasHistory] = useState(false);
    const printRef = useRef(null);
    const [exportOptions, setExportOptions] = useState({ includePhoto: true, singleColumn: false, fontSize: 'normal' });
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const handlePrint = useReactToPrint({ contentRef: printRef });

    const [editOpen, setEditOpen] = useState(false);
    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
    const canEdit = ['editor', 'admin'].includes(currentUser.role) || !recipeData?.created_by || recipeData?.created_by === currentUser.id;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [shareAnchorEl, setShareAnchorEl] = useState(null);
    const [multiplier, setMultiplier] = useState(1);
    const [customMultiplierAnchor, setCustomMultiplierAnchor] = useState(null);
    const [customMultiplierInput, setCustomMultiplierInput] = useState('');
    const open = Boolean(anchorEl);

    const [collections, setCollections] = useState([]);
    const [addToColOpen, setAddToColOpen] = useState(false);
    const [addedSnack, setAddedSnack] = useState(null); // collection name
    const [linkCopied, setLinkCopied] = useState(false);
    const [publicLinkOpen, setPublicLinkOpen] = useState(false);
    const [publicToken, setPublicToken] = useState(recipeData?.recipe?.public_token ?? null);
    const [publicLinkLoading, setPublicLinkLoading] = useState(false);
    const [publicLinkError, setPublicLinkError] = useState(false);
    const [pendingPrint, setPendingPrint] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [inSession, setInSession] = useState(false);
    useEffect(() => {
        setInSession(isInSession(id));
        const handler = () => setInSession(isInSession(id));
        window.addEventListener('cookSessionChanged', handler);
        return () => window.removeEventListener('cookSessionChanged', handler);
    }, [id]);

    useEffect(() => {
        if (pendingPrint) { setPendingPrint(false); handlePrint(); }
    }, [pendingPrint, handlePrint]);

    const handleStartCooking = () => {
        const recipe = recipeData?.recipe;
        if (!recipe) return;
        const alreadyCooking = getActiveRecipes().length > 0;
        addToSession(recipe);
        if (!alreadyCooking) navigate('/cook');
    };

    useEffect(() => {
        fetchCollections().then(setCollections).catch(() => {});
    }, []);

    const favoritesCol = collections.find(c => c.is_favorites && c.owner_id === JSON.parse(localStorage.getItem('user'))?.id);
    const isFavorited = favoritesCol?.recipe_ids?.includes(parseInt(id));

    const handleToggleFavorite = () => {
        if (!favoritesCol) return;
        const recipeId = parseInt(id);
        if (isFavorited) {
            setCollections(prev => prev.map(c =>
                c.id === favoritesCol.id ? { ...c, recipe_ids: c.recipe_ids.filter(r => r !== recipeId) } : c
            ));
            removeFromCollection(favoritesCol.id, id).catch(() => fetchCollections().then(setCollections));
        } else {
            setCollections(prev => prev.map(c =>
                c.id === favoritesCol.id ? { ...c, recipe_ids: [...c.recipe_ids, recipeId] } : c
            ));
            addToCollection(favoritesCol.id, id).catch(() => fetchCollections().then(setCollections));
        }
    };

    const handleAddToCollection = () => { handleClose(); setAddToColOpen(true); };

    const handleAddToCol = (col) => {
        const recipeId = parseInt(id);
        setCollections(prev => prev.map(c =>
            c.id === col.id ? { ...c, recipe_ids: [...c.recipe_ids, recipeId] } : c
        ));
        setAddToColOpen(false);
        setAddedSnack(col.name);
        addToCollection(col.id, id).catch(() => fetchCollections().then(setCollections));
    };

    const handleShowHistory = () => {
        setHistoryOpen(true);
        handleClose();
    };

    const groupIngredientsByCategory = (ingredients) => {
        return (ingredients || []).reduce((acc, ingredient) => {
            const category = ingredient.category_name || 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(ingredient);
            return acc;
        }, {});
    };

    const groupDirectionsByCategory = (directions) => {
        return (directions || []).reduce((acc, dir) => {
            const category = dir.category_name || 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(dir);
            return acc;
        }, {});
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => { setAnchorEl(null); setShareAnchorEl(null); };
    const handleShareOpen = (event) => setShareAnchorEl(event.currentTarget);
    const handleShareClose = () => setShareAnchorEl(null);
    const handleShareWithAnyone = () => { handleClose(); setPublicLinkOpen(true); };

    const handleGeneratePublicLink = async () => {
        setPublicLinkLoading(true);
        setPublicLinkError(false);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${id}/share`, {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + Cookies.get('token') },
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setPublicToken(data.public_token);
        } catch {
            setPublicLinkError(true);
        } finally {
            setPublicLinkLoading(false);
        }
    };

    const handleRevokePublicLink = async () => {
        setPublicLinkLoading(true);
        setPublicLinkError(false);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${id}/share`, {
                method: 'DELETE',
                headers: { Authorization: 'Bearer ' + Cookies.get('token') },
            });
            if (!res.ok) throw new Error();
            setPublicToken(null);
        } catch {
            setPublicLinkError(true);
        } finally {
            setPublicLinkLoading(false);
        }
    };
    const handleDeleteRecipe = async () => {
        setDeleteLoading(true);
        try {
            await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${Cookies.get('token')}` },
            });
            setDeleteDialogOpen(false);
            navigate('/');
        } finally {
            setDeleteLoading(false);
        }
    };
    const handleExport = () => { handleClose(); setExportDialogOpen(true); };
    const handleExportConfirm = (options) => { setExportOptions(options); setPendingPrint(true); };
    const handleCopyLink = () => { handleClose(); navigator.clipboard.writeText(window.location.href).then(() => setLinkCopied(true)); };
    const handleUploadImage = () => { setDialogOpen(true); handleClose(); };
    const handleEditRecipe = () => { setEditOpen(true); handleClose(); };

    const handleUpload = async (image) => {
        setLoading(true);
        const image_payload = { title: recipeData.recipe.title };
        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/update/${id}`;
        const headers = {
            'Authorization': 'Bearer ' + Cookies.get('token'),
            'Content-Type': 'application/json',
        };

        if (image !== "") {
            await new Promise((resolve, reject) => {
                uploadImage(
                    image,
                    (error) => { console.error(`Error: ${JSON.stringify(error)}`); reject(error); },
                    (imageData) => {
                        image_payload.image_url = imageData[0];
                        image_payload.download_image_url = imageData[1];
                        resolve();
                    }
                );
            });

            fetch(api, { method: 'PUT', headers, body: JSON.stringify(image_payload) })
                .then(response => {
                    if (!response.ok) throw new Error('Network response was not ok');
                    return response.json();
                })
                .then(() => {
                    const updatedTracked = { ...tracked };
                    delete updatedTracked.recipes[id];
                    sessionStorage.setItem("recipes", JSON.stringify(updatedTracked));
                    refreshRecipes(true);
                    setRefreshFlag(prev => !prev);
                })
                .catch(error => {
                    setLoading(false);
                    console.error('There was a problem updating the image:', error);
                });
        }
    };

    useEffect(() => {
        if (id) { setLoading(true); setError(null); }
    }, [id]);

    useEffect(() => {
        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${id}`;
        const headers = {
            'Authorization': 'Bearer ' + Cookies.get('token'),
            'Content-Type': 'application/json',
        };

        const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

        const handleTrackRecipe = (response) => {
            tracked.recipes[response.recipe.id] = { ...response, _cachedAt: Date.now() };
            sessionStorage.setItem("recipes", JSON.stringify(tracked));
        };

        async function fetchApi() {
            try {
                const data = await fetch(api, { method: 'GET', headers });
                if (data.status === 404) throw new Error('Recipe not found');
                const response = await data.json();
                setRecipeData(response);
                setPublicToken(response.recipe?.public_token ?? null);
                setLoading(false);
                handleTrackRecipe(response);
            } catch (error) {
                setLoading(false);
                setError(error.message);
            }
        }

        const cached = tracked.recipes[id];
        const isStale = !cached?._cachedAt || (Date.now() - cached._cachedAt) > CACHE_TTL_MS;

        if (!cached || isStale) {
            fetchApi();
        } else {
            setLoading(false);
        }
    }, [id, tracked, refreshFlag, setRecipeData]);

    useEffect(() => {
        if (!id) return;
        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${id}/versions`;
        fetch(api, {
            method: 'GET',
            headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => setHasHistory(data?.versions?.length > 0))
            .catch(() => setHasHistory(false));
    }, [id, refreshFlag]);

    if (loading) return (
        <Container sx={{ mt: 3, width: '90%', pb: 12 }}>
            {isSmallScreen ? (
                <>
                    {/* Mobile: image first, then title below */}
                    <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" width="70%" height={40} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="40%" height={24} />
                    <Box sx={{ mt: 4 }}>
                        <Skeleton variant="text" width="30%" height={32} />
                        <Skeleton variant="text" width="85%" sx={{ mt: 1 }} />
                        <Skeleton variant="text" width="75%" />
                        <Skeleton variant="text" width="80%" />
                    </Box>
                </>
            ) : (
                <>
                    {/* Desktop: title left, image right */}
                    <Grid container spacing={4} alignItems="center">
                        <Grid item md={7}>
                            <Skeleton variant="text" width="60%" height={48} />
                            <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item md={5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Skeleton variant="rectangular" width={280} height={280} sx={{ borderRadius: 2 }} />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 5 }}>
                        <Skeleton variant="text" width="20%" height={32} />
                        <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
                        <Skeleton variant="text" width="70%" />
                        <Skeleton variant="text" width="75%" />
                    </Box>
                </>
            )}
        </Container>
    );

    if (error) return (
        <Container sx={{ mt: 6, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error}</Typography>
        </Container>
    );

    const groupedIngredients = groupIngredientsByCategory(recipeData.recipe?.ingredients);
    const groupedDirections = groupDirectionsByCategory(recipeData.recipe?.directions);

    return (
        <Container component="main" sx={{ mt: 3, width: '90%', pb: 12 }}>
            <RecipePrint ref={printRef} recipe={recipeData?.recipe} multiplier={multiplier} includePhoto={exportOptions.includePhoto} singleColumn={exportOptions.singleColumn} fontSize={exportOptions.fontSize} />
            <ExportOptionsDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} onExport={handleExportConfirm} recipe={recipeData?.recipe} multiplier={multiplier} />

            {/* Delete Recipe confirmation — admin only */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete Recipe?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        <strong>{recipeData?.recipe?.title}</strong> will be hidden from all users. You can restore it from the Admin page if needed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleteLoading}>Cancel</Button>
                    <Button onClick={handleDeleteRecipe} variant="contained" color="error" disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share with Anyone dialog */}
            <Dialog open={publicLinkOpen} onClose={() => setPublicLinkOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><LinkIcon sx={{ color: 'primary.main' }} />Share with Anyone</DialogTitle>
                <DialogContent>
                    {publicToken ? (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Anyone with this link can view this recipe — no sign-in required.
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={`${window.location.origin}/r/${publicToken}`}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/r/${publicToken}`).then(() => setLinkCopied(true)); }}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Generate a public link so anyone — even without an account — can view this recipe.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Box>
                        {publicToken && (
                            <Button color="error" onClick={handleRevokePublicLink} disabled={publicLinkLoading}>
                                {publicLinkLoading ? <CircularProgress size={18} /> : 'Revoke link'}
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setPublicLinkOpen(false)} color="inherit">Close</Button>
                        {!publicToken && (
                            <Button variant="contained" onClick={handleGeneratePublicLink} disabled={publicLinkLoading}>
                                {publicLinkLoading ? <CircularProgress size={18} /> : 'Generate link'}
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Actions menu — top right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
                    <IconButton onClick={handleToggleFavorite} aria-label="favorite">
                        {isFavorited
                            ? <FavoriteIcon sx={{ color: 'error.main' }} />
                            : <FavoriteBorderIcon />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add to Collection">
                    <IconButton onClick={handleAddToCollection} aria-label="add to collection">
                        <CollectionsBookmarkIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="More options">
                    <IconButton
                        aria-label="recipe actions"
                        aria-controls="recipe-menu"
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreHorizIcon />
                    </IconButton>
                </Tooltip>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem onClick={handleShareOpen}>
                        <ListItemText primary="Share" />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>›</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleUploadImage}>
                        <ListItemText primary="Upload Image" />
                    </MenuItem>
                    {canEdit && (
                        <MenuItem onClick={handleEditRecipe}>
                            <ListItemText primary="Edit Recipe" />
                        </MenuItem>
                    )}
                    {hasHistory && (
                        <MenuItem onClick={handleShowHistory}>
                            <ListItemText primary="Show History" />
                        </MenuItem>
                    )}
                    {currentUser.role === 'admin' && [
                        <Divider key="del-divider" />,
                        <MenuItem key="del-item" onClick={() => { handleClose(); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>
                            <ListItemText primary="Delete Recipe" />
                        </MenuItem>,
                    ]}
                </Menu>
                <Menu
                    anchorEl={shareAnchorEl}
                    open={Boolean(shareAnchorEl)}
                    onClose={handleShareClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={handleCopyLink}>
                        <ListItemText primary="Copy Link" />
                    </MenuItem>
                    <MenuItem onClick={handleExport}>
                        <ListItemText primary="Export Recipe" />
                    </MenuItem>
                    <MenuItem onClick={handleShareWithAnyone}>
                        <ListItemText primary="Share with Anyone" secondary={publicToken ? 'Link active' : null} />
                    </MenuItem>
                </Menu>
            </Box>

            {/* Add to collection dialog */}
            <Dialog open={addToColOpen} onClose={() => setAddToColOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CollectionsBookmarkIcon sx={{ color: 'primary.main' }} />
                    Add to Collection
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0, pb: 1 }}>
                    {(() => {
                        const uid = JSON.parse(localStorage.getItem('user'))?.id;
                        const recipeId = parseInt(id);
                        const editable = collections.filter(c =>
                            c.owner_id === uid || c.shared_with?.some(s => s.shared_with_user_id === uid && s.permission === 'edit')
                        );
                        const mine = editable.filter(c => c.owner_id === uid && !c.is_favorites);
                        const shared = editable.filter(c => c.owner_id !== uid);
                        const allVisible = [...mine, ...shared];

                        const ColRow = ({ col, inCollection }) => (
                            <Box
                                key={col.id}
                                onClick={inCollection ? undefined : () => handleAddToCol(col)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 2, py: 1.25,
                                    borderRadius: 2, border: '1px solid',
                                    borderColor: inCollection ? 'success.main' : 'divider',
                                    bgcolor: inCollection ? 'success.50' : 'transparent',
                                    cursor: inCollection ? 'default' : 'pointer',
                                    opacity: inCollection ? 0.75 : 1,
                                    '&:hover': inCollection ? {} : { bgcolor: 'action.hover', borderColor: 'primary.main' },
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{col.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {col.recipe_ids.length} {col.recipe_ids.length === 1 ? 'recipe' : 'recipes'}
                                    </Typography>
                                </Box>
                                {inCollection && <CheckIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />}
                            </Box>
                        );

                        const Section = ({ label, cols }) => cols.length === 0 ? null : (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.75, display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: 10 }}>
                                    {label}
                                </Typography>
                                <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {cols.map(col => <ColRow key={col.id} col={col} inCollection={col.recipe_ids.includes(recipeId)} />)}
                                </Box>
                            </Box>
                        );

                        if (allVisible.length === 0) return (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No collections available.
                            </Typography>
                        );

                        return (
                            <>
                                <Section label="My Collections" cols={mine} />
                                <Section label="Collaborating On" cols={shared} />
                            </>
                        );
                    })()}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={() => setAddToColOpen(false)} color="inherit">Close</Button>
                </DialogActions>
            </Dialog>

            <UploadImageDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onUpload={handleUpload}
            />
            <RecipeEdit
                open={editOpen}
                onClose={(updatedRecipe) => {
                    if (updatedRecipe) {
                        setRecipeData((prevData) => ({ ...prevData, recipe: updatedRecipe }));
                        const storedRecipes = JSON.parse(sessionStorage.getItem("recipes")) || { recipes: {} };
                        storedRecipes.recipes[updatedRecipe.id] = { recipe: updatedRecipe };
                        sessionStorage.setItem("recipes", JSON.stringify(storedRecipes));
                        setRefreshFlag(prev => !prev);
                    }
                    setEditOpen(false);
                }}
                recipe={recipeData.recipe}
            />
            <RecipeHistory
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                recipeId={id}
            />

            <Snackbar
                open={Boolean(addedSnack)}
                autoHideDuration={3000}
                onClose={() => setAddedSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setAddedSnack(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
                    Added to "{addedSnack}"
                </Alert>
            </Snackbar>
            <Snackbar
                open={linkCopied}
                autoHideDuration={2500}
                onClose={() => setLinkCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setLinkCopied(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
                    Link copied!
                </Alert>
            </Snackbar>
            <Snackbar
                open={publicLinkError}
                autoHideDuration={4000}
                onClose={() => setPublicLinkError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setPublicLinkError(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
                    Something went wrong. Please try again.
                </Alert>
            </Snackbar>

            {/* Title + image hero */}
            <Grid container spacing={3} alignItems="center" sx={{ mt: 0 }}>
                {/* On mobile: image first, then text. On desktop: text left, image right */}
                <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {recipeData.recipe?.title}
                    </Typography>
                    {(recipeData.recipe?.author || recipeData.recipe?.authors?.length > 0) && (
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                            <AuthorLink
                                author={recipeData.recipe.author}
                                authorId={recipeData.recipe.author_id}
                                authorUserId={recipeData.recipe.author_user_id}
                                avatarUrl={recipeData.recipe.author_profile?.avatar_url}
                                authors={recipeData.recipe.authors}
                            />
                        </Typography>
                    )}
                    {(recipeData.recipe?.prep_time || recipeData.recipe?.recipe_yield) && (
                        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                            {recipeData.recipe?.prep_time && (
                                <Typography variant="body2" color="text.secondary">
                                    Prep time: {recipeData.recipe.prep_time}
                                </Typography>
                            )}
                            {recipeData.recipe?.recipe_yield && (
                                <Typography variant="body2" color="text.secondary">
                                    Yield: {recipeData.recipe.recipe_yield}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {recipeData.recipe?.recipe_description?.trim() && (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
                            {recipeData.recipe.recipe_description}
                        </Typography>
                    )}
                    {recipeData.recipe?.source_url && (
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                            Originally from{' '}
                            <a href={recipeData.recipe.source_url} target="_blank" rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}>
                                {new URL(recipeData.recipe.source_url).hostname.replace(/^www\./, '')}
                            </a>
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                    <RecipeImage
                        loading="lazy"
                        src={recipeData.recipe.download_image_url || '/anchovie.jpg'}
                        alt={recipeData.recipe.title}
                        sx={{
                            width: '100%',
                            maxWidth: { xs: '100%', md: 280 },
                            height: { xs: 240, md: 280 },
                            objectFit: 'cover',
                            borderRadius: 2,
                            display: 'block',
                        }}
                    />
                </Grid>
            </Grid>

            {/* Cook session button — constrained to ingredient column width on desktop */}
            <Box sx={{ width: { xs: '100%', md: '33.333%' }, mb: 2, mt: 3 }}>
                <Button
                    variant={inSession ? 'outlined' : 'contained'}
                    color="primary"
                    startIcon={<OutdoorGrillIcon />}
                    onClick={handleStartCooking}
                    disabled={inSession}
                    fullWidth
                >
                    {inSession ? 'In Cooking Session' : getActiveRecipes().length > 0 ? 'Add to Cooking Session' : 'Start Cooking'}
                </Button>
            </Box>

            {/* Body content — two columns on desktop */}
            <Grid container spacing={5} sx={{ mt: 0 }}>

                {/* Ingredients */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="h6">Ingredients</Typography>
                        <ToggleButtonGroup
                            value={[1, 2, 3].includes(multiplier) ? multiplier : 'custom'}
                            exclusive
                            onChange={(_, val) => {
                                if (val === 'custom' || val === null) return;
                                setMultiplier(val);
                            }}
                            size="small"
                        >
                            <ToggleButton value={1}>1x</ToggleButton>
                            <ToggleButton value={2}>2x</ToggleButton>
                            <ToggleButton value={3}>3x</ToggleButton>
                            <ToggleButton value="custom" onClick={(e) => {
                                setCustomMultiplierInput([1, 2, 3].includes(multiplier) ? '' : String(multiplier));
                                setCustomMultiplierAnchor(e.currentTarget);
                            }}>
                                {[1, 2, 3].includes(multiplier) ? 'Custom' : `${multiplier}x`}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Popover
                            open={Boolean(customMultiplierAnchor)}
                            anchorEl={customMultiplierAnchor}
                            onClose={() => setCustomMultiplierAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 180 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    Custom multiplier (0.25–20)
                                </Typography>
                                <TextField
                                    autoFocus
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0.25, max: 20, step: 0.25 }}
                                    value={customMultiplierInput}
                                    onChange={e => setCustomMultiplierInput(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            const raw = parseFloat(customMultiplierInput);
                                            if (!isNaN(raw) && raw > 0) {
                                                const snapped = Math.round(raw * 4) / 4;
                                                const clamped = Math.min(20, Math.max(0.25, snapped));
                                                setMultiplier(clamped);
                                                setCustomMultiplierAnchor(null);
                                            }
                                        }
                                    }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">x</InputAdornment> }}
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={(() => { const v = parseFloat(customMultiplierInput); return isNaN(v) || v <= 0; })()}
                                    onClick={() => {
                                        const raw = parseFloat(customMultiplierInput);
                                        if (!isNaN(raw) && raw > 0) {
                                            const snapped = Math.round(raw * 4) / 4;
                                            const clamped = Math.min(20, Math.max(0.25, snapped));
                                            setMultiplier(clamped);
                                            setCustomMultiplierAnchor(null);
                                        }
                                    }}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Popover>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {Object.entries(groupedIngredients).sort(([a, aIngs], [b, bIngs]) => {
                        if (a === 'Uncategorized') return 1;
                        if (b === 'Uncategorized') return -1;
                        return (aIngs[0]?.category_order ?? 0) - (bIngs[0]?.category_order ?? 0);
                    }).map(([category, ingredients]) => {
                        if (category === 'Uncategorized' && ingredients.length === 0) return null;
                        return (
                            <Box key={category} sx={{ mb: 3 }}>
                                {category !== 'Uncategorized' && (
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                        {category}
                                    </Typography>
                                )}
                                {ingredients.map((ing, index) => (
                                    <Typography key={index} variant="body1" sx={{ mb: 0.5 }}>
                                        &bull;&nbsp;{[scaleQuantity(ing.quantity, multiplier), ing.unit, ing.ingredient].filter(Boolean).join(' ')}
                                    </Typography>
                                ))}
                            </Box>
                        );
                    })}
                </Grid>

                {/* Directions */}
                {recipeData.recipe?.directions?.length > 0 && (
                    <Grid item xs={12} md={8}>
                        <SectionHeader>Directions</SectionHeader>
                        {(() => {
                            const entries = Object.entries(groupedDirections);
                            entries.sort(([a, aDirs], [b, bDirs]) => {
                                if (a === 'Uncategorized') return 1;
                                if (b === 'Uncategorized') return -1;
                                const orderA = aDirs[0]?.category_order ?? 0;
                                const orderB = bDirs[0]?.category_order ?? 0;
                                return orderA - orderB;
                            });
                            return entries.map(([category, dirs]) => {
                                const sorted = dirs.slice().sort((x, y) =>
                                    (x.direction_order ?? 0) - (y.direction_order ?? 0)
                                );
                                return (
                                    <Box key={`dir-cat-${category}`} sx={{ mb: 3 }}>
                                        {category !== 'Uncategorized' && (
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                {category}
                                            </Typography>
                                        )}
                                        {sorted.map((dir, index) => (
                                            <Typography key={index} variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                                                <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                                                    {dir.direction_order ?? (index + 1)}.
                                                </Box>
                                                {dir.direction}
                                            </Typography>
                                        ))}
                                    </Box>
                                );
                            });
                        })()}
                    </Grid>
                )}
            </Grid>
            <Divider sx={{ mt: 4 }} />
            <RecipeComments recipeId={id} />
        </Container>
    );
};

export default Recipe;
